import { Component } from '@theme/component';

/**
 * FAQ-by-topic scroll spy.
 *
 * The topic links are plain anchors, so jumping to a topic works with CSS alone
 * (scroll-behavior + scroll-margin) and needs no JS. What CSS cannot do is track
 * which topic is on screen as the shopper scrolls freely — :target only updates
 * on click. This component fills that gap by marking the visible topic's link
 * with aria-current, which the stylesheet paints as the active pill.
 *
 * The topmost group intersecting the viewport wins. A root margin pulls the top
 * edge below the sticky site header so a group counts as "current" only once it
 * has actually cleared the header.
 */
class HpFaqTopics extends Component {
  #observer = null;
  #resizeObserver = null;
  /** @type {Map<string, HTMLAnchorElement>} anchor id → topic link */
  #links = new Map();
  /** @type {Set<string>} ids of groups currently intersecting */
  #visible = new Set();
  /** @type {HTMLElement | null} the horizontally scrolling chip row (mobile) */
  #scroller = null;

  connectedCallback() {
    super.connectedCallback();

    this.#initOverflowHints();

    for (const link of this.querySelectorAll('.hp-faqtopics__link')) {
      const id = decodeURIComponent(link.getAttribute('href')?.slice(1) ?? '');
      if (id) this.#links.set(id, link);
    }

    const groups = [...this.querySelectorAll('.hp-faqtopics__group')].filter((group) => group.id);
    if (!groups.length || !this.#links.size) return;

    // Answers expand and collapse, so group heights change — re-run on toggle.
    this.addEventListener('toggle', this.#onToggle, { capture: true });

    this.#observer = new IntersectionObserver(this.#onIntersect, {
      rootMargin: `-${this.#headerOffset}px 0px -60% 0px`,
    });

    for (const group of groups) this.#observer.observe(group);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#observer?.disconnect();
    this.#resizeObserver?.disconnect();
    this.#scroller?.removeEventListener('scroll', this.#updateOverflowHints);
    this.removeEventListener('toggle', this.#onToggle, { capture: true });
  }

  /**
   * The chip row hides its scrollbar, so nothing signals that it scrolls. Mark
   * the nav with the edges that still have content behind them and let the
   * stylesheet fade them. Desktop stacks the chips vertically, where the
   * scroller never overflows and both hints stay off on their own.
   */
  #initOverflowHints() {
    this.#scroller = this.querySelector('[data-scroller]');
    if (!this.#scroller) return;

    this.#scroller.addEventListener('scroll', this.#updateOverflowHints, { passive: true });

    // Chip widths depend on fonts and the viewport, so re-measure on resize.
    this.#resizeObserver = new ResizeObserver(this.#updateOverflowHints);
    this.#resizeObserver.observe(this.#scroller);

    this.#updateOverflowHints();
  }

  #updateOverflowHints = () => {
    const scroller = this.#scroller;
    const wrap = scroller?.closest('.hp-faqtopics__list-wrap');
    if (!scroller || !wrap) return;

    const { scrollLeft, scrollWidth, clientWidth } = scroller;
    // Sub-pixel layout can leave a fraction of a pixel unscrolled at each end.
    // scrollLeft goes negative in RTL, so compare on its magnitude.
    const start = Math.abs(scrollLeft) > 1;
    const end = Math.abs(scrollLeft) + clientWidth < scrollWidth - 1;

    wrap.toggleAttribute('data-overflow-start', start);
    wrap.toggleAttribute('data-overflow-end', end);
  };

  /** Height of the sticky site header, so a group only counts once it clears it. */
  get #headerOffset() {
    const raw = getComputedStyle(document.body).getPropertyValue('--header-height');
    return Number.parseInt(raw, 10) || 0;
  }

  #onIntersect = (entries) => {
    for (const entry of entries) {
      if (entry.isIntersecting) this.#visible.add(entry.target.id);
      else this.#visible.delete(entry.target.id);
    }
    this.#render();
  };

  /**
   * Opening an answer shifts every group below it, which the observer only
   * notices on the next scroll. Nudge it by re-reading positions right away.
   */
  #onToggle = () => {
    for (const [id] of this.#links) {
      const group = document.getElementById(id);
      if (!group) continue;
      const { top, bottom } = group.getBoundingClientRect();
      const inView = top < window.innerHeight * 0.4 && bottom > this.#headerOffset;
      if (inView) this.#visible.add(id);
      else this.#visible.delete(id);
    }
    this.#render();
  };

  /** Mark the topmost visible group's link as current. */
  #render() {
    let currentId = null;
    let currentTop = Infinity;

    for (const id of this.#visible) {
      const top = document.getElementById(id)?.getBoundingClientRect().top ?? Infinity;
      if (top < currentTop) {
        currentTop = top;
        currentId = id;
      }
    }

    // Before the first group scrolls in (and past the last one), keep the
    // nearest topic highlighted rather than clearing the nav entirely.
    if (!currentId) currentId = this.#lastPassedId() ?? this.#links.keys().next().value ?? null;

    for (const [id, link] of this.#links) {
      if (id === currentId) link.setAttribute('aria-current', 'true');
      else link.removeAttribute('aria-current');
    }
  }

  /** The last group whose top has scrolled above the header. */
  #lastPassedId() {
    let passedId = null;
    for (const [id] of this.#links) {
      const top = document.getElementById(id)?.getBoundingClientRect().top;
      if (top != null && top <= this.#headerOffset) passedId = id;
    }
    return passedId;
  }
}

if (!customElements.get('hp-faq-topics')) {
  customElements.define('hp-faq-topics', HpFaqTopics);
}
